import { forwardRef, useEffect, useImperativeHandle, useRef, ReactNode, useState } from 'react';
import type { Entry, RegistrableApp, MicroAppStateActions } from 'qiankun';
import { loadMicroApp, initGlobalState } from 'qiankun';
import { whites, minWhites } from './white.config';
import store from '@/store/store';
import { createBrowserHistory } from 'history';

const history = createBrowserHistory();


const formatToHump = (value) => {
  return value.replace(/\-(\w)/g, (_, letter) => letter.toUpperCase());
};

const excludeAssetFilter = (assetUrl: string) => {
  const assets: any = [].concat(Object.values(whites as any)).concat(Object.values(minWhites as any));
  // 子应用不需要重复加载，主应用加载就够了, 微应用资源（css/js) 不被 qiankun 劫持处理
  return assets.includes(assetUrl);
  // 白名单的资源不解析这样就不会重复加载
};


/** qiankun的配置项 */
interface Options {
  singular?: boolean | ((app: RegistrableApp<any>) => Promise<boolean>);
  // 是否为单实例场景，单实例指的是同一时间只会渲染一个微应用。默认为 false。
  fetch?: typeof fetch;
  // 自定义的 fetch 方法。
  getPublicPath?: (entry: Entry) => string;
  // 微应用的 entry 值。
  getTemplate?: (template: string) => string;
  // 获取模版，一般是html-entry-imort设置的模版
  excludeAssetFilter?: (assetUrl: string) => boolean;
  // 可选，指定部分特殊的动态加载的微应用资源（css/js) 不被 qiankun 劫持处理
}

interface MicroApplication {
   // 微应用的基础信息
  name: string;
  // 微应用的名称，微应用之间必须确保唯一。
  entry: string | { scripts?: string[]; styles?: string[]; html?: string };
  // 必选，微应用的入口（详细说明同上）。
  container: string | HTMLElement;
  // 微应用的容器节点的选择器或者 Element 实例。如container: '#root' 或 container: document.querySelector('#root')。
  props?: Record<string, any>;
  // 初始化时需要传递给微应用的数据。
  //
  sandbox?: Sandbox;
  // 默认情况下沙箱可以确保单实例场景子应用之间的样式隔离，但是无法确保主应用跟子应用、或者多实例场景的子应用样式隔离。当配置为 { strictStyleIsolation: true } 时表示开启严格的样式隔离模式。这种模式下 qiankun 会为每个微应用的容器包裹上一个 shadow dom 节点，从而确保微应用的样式不会对全局造成影响。
  singular?: boolean;
  }

type Sandbox = boolean | { strictStyleIsolation?: boolean, experimentalStyleIsolation?: boolean }

// 用于加载乾坤的逻辑
function loadMicroApplication(app: MicroApplication, options?: Options): MicrApp {
  return loadMicroApp(app, {
    ...options,
    sandbox: {
      strictStyleIsolation: true,
      experimentalStyleIsolation: true,
    },
    excludeAssetFilter
  } as any,
  );
};

interface MicroApplicationRouteProps {
  pathname: string; // 指定路由的路径
  entry: string; // 指定微应用的html入口
  props?: Record<string, any> // 额外传给微应用的参数(主要用于业务的定制化)
  fallback?: ReactNode;
  onStateChange?: (state, prev) => void;
  state?: any;
  onMicroAppChange?: (micrApp: any) => void;
};

interface MicrApp {
  mount?: () => Promise<null>;
  unmount?: () => Promise<null>;
  update?: (customProps: Record<string, any>) => Promise<any>;
  getStatus?: () => | 'NOT_LOADED' | 'LOADING_SOURCE_CODE' | 'NOT_BOOTSTRAPPED' | 'BOOTSTRAPPING' | 'NOT_MOUNTED' | 'MOUNTING' | 'MOUNTED' | 'UPDATING' | 'UNMOUNTING' | 'UNLOADING' | 'SKIP_BECAUSE_BROKEN' | 'LOAD_ERROR';
  loadPromise?: Promise<null>;
  bootstrapPromise?: Promise<null>;
  mountPromise?: Promise<null>;
  unmountPromise?: Promise<null>;
}

// 仅在微应用初始化时执行一次
const actions: MicroAppStateActions = initGlobalState();

const MicroApplicationRoute = ({ pathname, entry, onMicroAppChange, props }: MicroApplicationRouteProps, ref) => {
  const realPathname = pathname.indexOf('/') > -1 ? pathname : `/${pathname}`;
  // container取路径删除/以后的部分
  const name = realPathname.slice(1,);
  const microRoot = useRef();
  const micrApp = useRef<any>(null);
  const [errored, setErrored] = useState<boolean>(false);
  // 由于路由一般是中划线，将其转换为符合规范的路由名称
  const appName = formatToHump(name);

  function loadApp(appName: string, container: string, entry: string, options?: Record<string, any>) {
    const props = {
      ...options,
      store,
      mainAppHistory: history,
    };
    micrApp.current = loadMicroApplication({
      entry,
      props,
      name: appName,
      container,
    });
    onMicroAppChange?.(micrApp.current);
    const { getStatus } = micrApp.current;
    // setLoading(true);
    const status = getStatus();
    if (status === 'LOADING_SOURCE_CODE') {
      micrApp.current.mountPromise.then(() => {
      }).catch(() => setErrored(true));
    } else if (status === 'LOAD_ERROR') {
      console.log('子应用加载失败');
      setErrored(false);
    } else if (status === 'UNMOUNTING') {
      micrApp.current.unmountPromise.then(() => {
        setErrored(false);
        // 微应用卸载
      }).cache(() => {
        console.log('子应用在卸载时报错了');
      });
    }
  };

  // 暴露一些外部用于执行微应用的钩子函数
  useImperativeHandle(ref as any, () => {
    const { mount, unmount, update } = micrApp.current || {};
    return {
      micrAppMount: mount,
      micrAppUmount: unmount,
      micrAppUpdate: update,
      // 考虑到不知道什么时候进行set值，因此抛给业务方方自行调用
      setState: (state: any) => actions.setGlobalState(state),
      offStateChange: actions.offGlobalStateChange,
    };
  });

  useEffect(() => {
    // 挂载微应用;
    micrApp.current = loadApp(appName, microRoot.current!, entry, props);
  }, [appName, entry, props]);


  return (
    <>
      <div ref={microRoot as any}></div>
      {errored && <p>微应用加载出错了</p>}
    </>
  );

};

const ForwardMicroApplicationRoute = forwardRef(MicroApplicationRoute);
ForwardMicroApplicationRoute.displayName = 'MicroApplicationRoute';
export default ForwardMicroApplicationRoute;
